// Import core lib dependencies.
import { createSelector } from 'reselect';

// Config.
import Config from './config/config.js';

// Misc dependencies.
import ConfigUtil from './utils/config.js';

/**
 * <pre>
 * UgcWall module Selectors(s).
 *
 * Summary of responsibilities:
 *
 *  - defines reusable selectors to retrieve state properties
 * </pre>
 *
 * @author Tim Loram
 *
 * @memberof module:UgcWall
 * @namespace
 *
 * @type {Object}
 */
const Selectors = {};

/**
 * Selector to access items property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const itemsSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].items;
Selectors.getItems = createSelector([itemsSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access customItems property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const customItemsSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].customItems;
Selectors.getCustomItems = createSelector([customItemsSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access activeItem property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const activeItemSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].activeItem;
Selectors.getActiveItem = createSelector([activeItemSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access paging property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const pagingSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].paging;
Selectors.getPaging = createSelector([pagingSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access sorting property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const sortingSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].sorting;
Selectors.getSorting = createSelector([sortingSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access filtering property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const filteringSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].filtering;
Selectors.getFiltering = createSelector([filteringSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access isActiveState property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const isActiveStateSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].isActiveState;
Selectors.getIsActiveState = createSelector([isActiveStateSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access polling properties of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const pollingSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].polling;
Selectors.getPolling = createSelector([pollingSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access collectionId property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const collectionIdSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].collectionId;
Selectors.getCollectionId = createSelector([collectionIdSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access useExternalLinkHandler property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const useExternalLinkHandlerSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].useExternalLinkHandler;
Selectors.getUseExternalLinkHandler = createSelector([useExternalLinkHandlerSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access hasFetched property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const hasFetchedSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].hasFetched;
Selectors.getHasFetched = createSelector([hasFetchedSelector], stateVal => {
  return stateVal;
});

/**
 * Selector to access isFetching property of Redux state for this module.
 *
 * @memberof module:UgcWall.Selectors
 *
 * @param  {Object}  state  The Redux state.
 * @param  {String}  componentType 	The module type.
 * @param  {String}  id 	The module id.
 * @return {Object}  The value of the retrieved state property.
 */
const isFetchingSelector = (state, componentType, id) =>
  state[Config.stateKey][ConfigUtil.getStateKeyForModuleType(componentType)][id].isFetching;
Selectors.getIsFetching = createSelector([isFetchingSelector], stateVal => {
  return stateVal;
});

export default Selectors;
